import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import LanguageToggle from "@/components/LanguageToggle";
import { ArrowRight } from "lucide-react";

const HUMMUS_LOGO = "https://customer-assets.emergentagent.com/job_3bbf64d7-5ea8-4462-b21f-8a2eab53d2ca/artifacts/z1p5466w_lama%20hummus%20logo.png";
const DIST_LOGO = "https://customer-assets.emergentagent.com/job_3bbf64d7-5ea8-4462-b21f-8a2eab53d2ca/artifacts/n1pebnkt_lama%20distribution%20logo.png";

// Background images chosen for warmth
const DINING_BG = "https://images.unsplash.com/photo-1601050690597-df0568f70950?auto=format&fit=crop&w=1600&q=80";
const DIST_BG = "https://images.unsplash.com/photo-1502741384106-56538427cde9?auto=format&fit=crop&w=1600&q=80";

export default function Gateway() {
  const { tr } = useLang();

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain relative overflow-hidden" data-testid="page-gateway">
      {/* Top bar */}
      <header className="absolute top-0 inset-x-0 z-30 px-6 md:px-10 py-6 flex items-center justify-between text-[#FDFBF7] mix-blend-difference">
        <div className="font-serif text-2xl tracking-tight">Lama</div>
        <LanguageToggle variant="light" />
      </header>

      {/* Split screen */}
      <div className="flex flex-col md:flex-row min-h-screen">
        {/* Dining */}
        <Link
          to="/dining"
          className="group relative flex-1 min-h-[55vh] md:min-h-screen overflow-hidden border-b md:border-b-0 md:border-r border-[#E5E0D8]"
          data-testid="gateway-dining-link"
        >
          <div
            className="absolute inset-0 bg-cover bg-center transition-transform duration-[1400ms] ease-out group-hover:scale-105"
            style={{ backgroundImage: `url(${DINING_BG})` }}
          />
          <div className="absolute inset-0 bg-[#1A1A1A]/55 group-hover:bg-[#1A1A1A]/40 transition-colors duration-700" />

          <div className="relative h-full flex flex-col items-center justify-center px-8 py-16 text-[#FDFBF7]">
            <img
              src={HUMMUS_LOGO}
              alt="Lama Hummus"
              className="w-28 h-28 md:w-36 md:h-36 rounded-full bg-[#1A1A1A] object-cover shadow-[0_8px_40px_rgba(0,0,0,0.4)]"
            />
            <div className="mt-8 text-[11px] uppercase tracking-[0.3em] text-[#FDFBF7]/70">
              {tr("nav.dining")}
            </div>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-center leading-none">
              {tr("gateway.diningTitle")}
            </h2>
            <p className="mt-4 text-sm md:text-base text-center max-w-xs text-[#FDFBF7]/80">
              {tr("gateway.diningSub")}
            </p>
            <span className="mt-10 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] border-b border-[#FDFBF7]/60 pb-1 group-hover:border-[#FDFBF7] transition-colors">
              {tr("gateway.diningCta")}
              <ArrowRight className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-2" />
            </span>
          </div>
        </Link>

        {/* Distribution */}
        <Link
          to="/distribution"
          className="group relative flex-1 min-h-[55vh] md:min-h-screen overflow-hidden"
          data-testid="gateway-distribution-link"
        >
          <div
            className="absolute inset-0 bg-cover bg-center transition-transform duration-[1400ms] ease-out group-hover:scale-105"
            style={{ backgroundImage: `url(${DIST_BG})` }}
          />
          <div className="absolute inset-0 bg-[#1A1A1A]/55 group-hover:bg-[#1A1A1A]/40 transition-colors duration-700" />

          <div className="relative h-full flex flex-col items-center justify-center px-8 py-16 text-[#FDFBF7]">
            <img
              src={DIST_LOGO}
              alt="Lama Distribution"
              className="w-28 h-28 md:w-36 md:h-36 rounded-full bg-[#1A1A1A] object-cover shadow-[0_8px_40px_rgba(0,0,0,0.4)]"
            />
            <div className="mt-8 text-[11px] uppercase tracking-[0.3em] text-[#FDFBF7]/70">
              {tr("nav.distribution")}
            </div>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-center leading-none">
              {tr("gateway.distTitle")}
            </h2>
            <p className="mt-4 text-sm md:text-base text-center max-w-xs text-[#FDFBF7]/80">
              {tr("gateway.distSub")}
            </p>
            <span className="mt-10 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] border-b border-[#FDFBF7]/60 pb-1 group-hover:border-[#FDFBF7] transition-colors">
              {tr("gateway.distCta")}
              <ArrowRight className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-2" />
            </span>
          </div>
        </Link>
      </div>

      {/* Tagline */}
      <div
        className="absolute bottom-6 inset-x-0 text-center text-[11px] uppercase tracking-[0.35em] text-[#FDFBF7]/80 z-30 mix-blend-difference px-6"
        data-testid="gateway-tagline"
      >
        {tr("gateway.tagline")}
      </div>
    </div>
  );
}
